import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import RoleService from '../../../Api/services/role.service';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type RoleResponse = components['schemas']['RoleResponse'];
type CreateRole = components['schemas']['CreateRole'];
type UpdateRole = components['schemas']['UpdateRole'];

interface RoleFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  role: RoleResponse | null;
  isEditing: boolean;
  onSuccess: () => void;
}

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  isOpen,
  toggle,
  role,
  isEditing,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateRole>>({
    name: '',
    is_public: false,
  });

  useEffect(() => {
    if (isOpen) {
      if (isEditing && role) {
        setFormData({
          name: role.name || '',
          is_public: role.is_public || false,
        });
      } else {
        setFormData({
          name: '',
          is_public: false,
        });
      }
    }
  }, [isOpen, role, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.name.trim()) {
      toast.error(t('El nombre es requerido'));
      return;
    }

    try {
      setLoading(true);
      if (isEditing && role?.id) {
        const updateData: UpdateRole = {
          id: role.id,
          name: formData.name.trim(),
          is_public: formData.is_public || false,
        };
        await RoleService.updateRole(updateData);
        toast.success(t('Rol actualizado exitosamente'));
      } else {
        const createData: CreateRole = {
          name: formData.name.trim(),
          is_public: formData.is_public || false,
        };
        await RoleService.createRole(createData);
        toast.success(t('Rol creado exitosamente'));
      }
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al guardar rol');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {isEditing ? t('Editar Rol') : t('Nuevo Rol')}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>{t('Nombre')} *</Label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('Nombre del rol')}
              required
            />
          </FormGroup>
          <FormGroup>
            <div className="form-check form-switch">
              <Input
                type="checkbox"
                className="form-check-input"
                checked={formData.is_public || false}
                onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
              />
              <Label className="form-check-label">{t('Rol Público')}</Label>
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={toggle} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('Guardando...')}
              </>
            ) : (
              t('Aceptar')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default RoleFormModal;

